import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { Address } from "@solomon-labs/design/components/ui";
import React, { useMemo, useRef } from "react";

import { useAddressFit } from "@/pages/shared/addressFit";

/**
 * The connected-wallet hint under the address field:
 * "[badge] [wallet] connected. [message]", `small`, on one line. The wallet is
 * the DS `Address` in the sentence's own face, size and colour
 * (`textVariant="small"`, `mono={false}`); it shows in full when it fits
 * beside the badge and the rest of the sentence, else steps down the DS
 * rungs (8...8, 4..4, 2..4). The line never widens its column.
 */
export function ConnectedWalletHint({
    wallet,
    message,
    tone,
    leading
}: {
    wallet: string;
    /** Follows "[wallet] connected."; omit for the bare connected line. */
    message?: string;
    tone: "error" | "muted";
    /** Shown before the sentence on the same line (e.g. a status badge). */
    leading?: React.ReactNode;
}): React.ReactElement {
    const lineRef = useRef<HTMLParagraphElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const leadingRef = useRef<HTMLSpanElement>(null);
    const suffixRef = useRef<HTMLSpanElement>(null);
    const reserve = useMemo(() => [leadingRef, suffixRef], []);
    // Re-fit whenever the line, the badge or the sentence changes width.
    const tier = useAddressFit(
        wallet,
        lineRef,
        measureRef,
        true,
        reserve,
        "snap"
    );

    return (
        <Text
            ref={lineRef}
            variant="small"
            role={tone === "error" ? "alert" : undefined}
            // contain-inline-size: the unwrapped line must not size its column.
            className={`relative mt-3 flex w-full items-center whitespace-nowrap contain-inline-size ${
                tone === "error" ? "text-error-base" : "text-foreground-muted"
            }`}
        >
            {/* The full address, unrendered, for its width in this face. */}
            <span
                ref={measureRef}
                aria-hidden
                className="invisible absolute top-0 left-0 whitespace-nowrap"
            >
                {wallet}
            </span>
            {leading ? (
                <span ref={leadingRef} className="inline-flex shrink-0 pr-2">
                    {leading}
                </span>
            ) : null}
            <Address
                value={wallet}
                tiers={[tier]}
                textVariant="small"
                mono={false}
                className="min-w-0"
            />
            <span ref={suffixRef} className="shrink-0 pl-1">
                connected.{message ? ` ${message}` : ""}
            </span>
        </Text>
    );
}
